import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, shareReplay } from 'rxjs';
import { API_BASE_URL } from '../../shared/api/api.tokens';
import { API_ENDPOINTS } from '../../shared/common/apiConstants';
import {
  BusinessPublicInfo,
  PublicAgendaEvent,
  PublicScheduleBlock,
  PublicBookingRequestPayload,
} from '../../shared/interfaces';

@Injectable({ providedIn: 'root' })
export class PublicBookingService {
  private http = inject(HttpClient);
  private baseUrl = inject(API_BASE_URL);

  // Cache for business info (5 minutes)
  private businessCache = new Map<string, { data: Observable<BusinessPublicInfo>; timestamp: number }>();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Obtiene información pública del negocio por su slug (con cache)
   */
  getBusinessBySlug(slug: string): Observable<BusinessPublicInfo> {
    const cached = this.businessCache.get(slug);
    const now = Date.now();

    if (cached && (now - cached.timestamp) < this.CACHE_DURATION) {
      return cached.data;
    }

    const request = this.http.get<BusinessPublicInfo>(
      `${this.baseUrl}/api/v1/public/sites/${slug}`
    ).pipe(shareReplay(1));

    this.businessCache.set(slug, { data: request, timestamp: now });
    return request;
  }

  /**
   * Limpia el cache de información del negocio
   */
  clearBusinessCache(slug?: string): void {
    if (slug) {
      this.businessCache.delete(slug);
    } else {
      this.businessCache.clear();
    }
  }

  /**
   * Obtiene servicios públicos de un negocio por slug
   */
  getBusinessServices(slug: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.baseUrl}/api/v1/public/sites/${slug}/services`
    );
  }

  /**
   * Obtiene profesionales asignados a un servicio de un negocio por slug
   */
  getProfessionalsByService(slug: string, serviceId: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.baseUrl}/api/v1/public/sites/${slug}/services/${serviceId}/professionals`
    );
  }

  /**
   * Obtiene el horario del negocio por slug
   */
  getBusinessSchedule(slug: string): Observable<{
    businessId: string;
    timeRanges: Array<{
      businessScheduleId: string;
      dayOfWeek: number;
      startTime: string;
      endTime: string;
      isActive: boolean;
    }>;
  }> {
    return this.http.get<{
      businessId: string;
      timeRanges: Array<{
        businessScheduleId: string;
        dayOfWeek: number;
        startTime: string;
        endTime: string;
        isActive: boolean;
      }>;
    }>(`${this.baseUrl}/api/v1/public/sites/${slug}/schedule`);
  }

  /**
   * Obtiene eventos bloqueados para un negocio por slug
   */
  getBusinessEvents(
    slug: string,
    startDate?: string,
    endDate?: string,
    professionalId?: string | null
  ): Observable<PublicAgendaEvent[]> {
    let params = new HttpParams();
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    if (professionalId) params = params.set('professionalId', professionalId);

    return this.http.get<PublicAgendaEvent[]>(
      `${this.baseUrl}/api/v1/public/sites/${slug}/events`,
      { params }
    );
  }

  /**
   * Bloqueos de agenda del negocio entre dos fechas (yyyy-MM-dd).
   * Con professionalId incluye los de ese profesional y los generales del negocio.
   */
  getBusinessBlocks(
    slug: string,
    startDate: string,
    endDate: string,
    professionalId?: string | null
  ): Observable<PublicScheduleBlock[]> {
    let params = new HttpParams().set('startDate', startDate).set('endDate', endDate);
    if (professionalId) params = params.set('professionalId', professionalId);

    return this.http.get<PublicScheduleBlock[]>(
      `${this.baseUrl}/api/v1/public/sites/${slug}/blocks`,
      { params }
    );
  }

  /**
   * Crea una reserva desde el micrositio público
   */
  createBooking(slug: string, payload: PublicBookingRequestPayload): Observable<{
    message: string;
    eventId: string;
    preferredStart: string;
    preferredEnd: string;
  }> {
    return this.http.post<{
      message: string;
      eventId: string;
      preferredStart: string;
      preferredEnd: string;
    }>(`${this.baseUrl}/api/v1/public/sites/${slug}/bookings`, payload);
  }
}
